/**
 * AgendaWidget — "Afspraken van vandaag". Zelfvoorzienend: haalt de Outlook-agenda
 * op (primair) en valt terug op Airtable-taken (PrioNr Afspraak/Draaidag) wanneer
 * Outlook niets/geen toegang geeft. Eerder inline in CockpitLite; nu een widget.
 *
 * Instellingen (per widget-instance): showLocation, hideAllDay.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { initCalendarService, getTodayCalendarEvents } from '../../../utils/microsoftCalendarService';
import { T } from './widgetTokens';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr); const t = new Date();
  return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
};

const notCancelled = (s) => !(s || '').toLowerCase().includes('geannuleerd');

export function AgendaSettings({ settings, onChange }) {
  const sw = (key, label) => (
    <FormControlLabel
      sx={{ m: 0, display: 'flex', justifyContent: 'space-between', '& .MuiFormControlLabel-label': { fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text } }}
      labelPlacement="start"
      control={<Switch size="small" checked={!!settings[key]} onChange={(e) => onChange({ [key]: e.target.checked })} />}
      label={label}
    />
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem', color: T.text, mb: 0.5 }}>
        Agenda-instellingen
      </Typography>
      {sw('showLocation', 'Locatie tonen')}
      {sw('hideAllDay', 'Hele-dag verbergen')}
    </Box>
  );
}

export default function AgendaWidget({ settings = {} }) {
  const { instance, accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const [outlookEvents, setOutlookEvents] = useState(null); // null = laden
  const [airtableAppts, setAirtableAppts] = useState([]);

  // Outlook (primair)
  useEffect(() => {
    let active = true;
    const account = accounts?.[0];
    if (instance && account) {
      initCalendarService(instance, account);
      getTodayCalendarEvents().then((ev) => { if (active) setOutlookEvents(ev || []); }).catch(() => { if (active) setOutlookEvents([]); });
    } else {
      setOutlookEvents([]);
    }
    return () => { active = false; };
  }, [instance, accounts]);

  // Airtable-fallback (alleen gebruikt als Outlook leeg is)
  useEffect(() => {
    let active = true;
    if (!userEmail) return undefined;
    authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        if (!active) return;
        const appts = (data.tasks || [])
          .map((t) => t.fields || {})
          .filter((f) => !f.Afgerond && !['Opgestuurd', 'Gearchiveerd'].includes(f.PrioNr))
          .filter((f) => ['Afspraak', 'Draaidag'].includes(f.PrioNr))
          .filter((f) => isToday(f.StartDateUitvoerder) || isToday(f.Deadline))
          .map((f) => ({ subject: f.NaamTaak || f.PrioNr, type: f.PrioNr, start: null, allDay: true }))
          .filter((a) => notCancelled(a.subject));
        setAirtableAppts(appts);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [userEmail]);

  const outlookSorted = (outlookEvents || [])
    .filter((e) => notCancelled(e.subject))
    .slice().sort((a, b) => (a.start || '').localeCompare(b.start || ''));
  const useOutlook = outlookSorted.length > 0;
  let appts = useOutlook ? outlookSorted : airtableAppts;
  if (settings.hideAllDay) appts = appts.filter((a) => !a.allDay);

  const loading = outlookEvents === null;

  return (
    <Box>
      <Typography sx={{
        fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.outline,
        textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5,
      }}>
        {new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={18} sx={{ color: T.outlineVariant }} />
        </Box>
      ) : appts.length === 0 ? (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>
          Geen afspraken vandaag.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {appts.map((a, i) => {
            const isShoot = a.type === 'Draaidag';
            const accent = isShoot ? T.amber : T.primary;
            return (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.5, borderLeft: `3px solid ${accent}`,
              }}>
                <Box sx={{ width: 58, flexShrink: 0, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 800, color: accent }}>
                    {a.allDay ? 'Hele dag' : fmtTime(a.start)}
                  </Typography>
                  {!a.allDay && a.end && (
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline }}>
                      tot {fmtTime(a.end)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
                    {a.subject}
                  </Typography>
                  {settings.showLocation && (a.location || a.type) && (
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.15 }}>
                      {a.location || a.type}
                    </Typography>
                  )}
                </Box>
                {isShoot && (
                  <Typography sx={{
                    fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, color: T.amber,
                    bgcolor: '#fef3c7', px: 1, py: 0.3, borderRadius: '9999px', flexShrink: 0,
                  }}>
                    SHOOT
                  </Typography>
                )}
              </Box>
            );
          })}
          {!useOutlook && airtableAppts.length > 0 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant, mt: 0.5 }}>
              Uit je taken (Outlook-agenda niet beschikbaar).
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
